import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "src/environments/environment";
import { getErrorMessage, getTranslationErrorIndexByKey } from "../functions/errors.functions";
import { ApiResponse } from "../models/interfaces/response";
import { BaseRequest } from "../models/requests/base.request";


@Injectable()
export class RestService {

  //#region DECLARATIONS

  private baseURL: string = environment.apiURL;

  private apiKey: string = environment.apiKey;

  //#endregion

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private router: Router,
  ) { }

  private urlComposer(endpoint: string): string {
    return `${this.baseURL}${endpoint}`;
  }

  /**
   * ## Rest Service Get Http Options Function
   * build and return the headers options for http request
   * @returns {HttpHeaders} Object option with headers information
   */
  private getHttpOptions(): Object {
    const httpOptions: Object = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'x-api-key': this.apiKey,
      }),
      withCredentials: true,
    };
    return httpOptions;
  }

  /**
   * ## Rest Service Post
   * Invoke the posts endpoint for `IDV360 Application Backend`
   * @param endpoint {String} the backend method name for request
   * @param data {BaseRequest} Object containing the request body
   * @returns {Promise<T>} Generic Promise with defined object in method invocation
   *
   * @usage from components specify the code as below
   * ```typescript
   * const myResponse = await this.restService.post<MyObject>("my-endpoint-here", MyRequestOject);
   * ```
   * the above representation will return a `Promise<MyObject>` since `MyObject` was defined as Generic Object
   */
  public post<T>(endpoint: string, data: BaseRequest): Promise<T> {
    return new Promise((resolve, reject) => {
      this.http.post(this.urlComposer(endpoint), data, this.getHttpOptions()).subscribe(response => {
        try {
          let data = <ApiResponse>response;
          this.checkResponseStatus(data);
          resolve(<T>data.message);
        }
        catch (error) {
          reject(new Error(getErrorMessage(error)));
        }
      }, (error: HttpErrorResponse) => {
        reject(new Error(this.resolveTranslation(error.error.message)));
      });
    });
  }

  /**
   * ## Rest Service Get
   * Fetch the endpoint for `IDV360 Application Backend`
   * @param endpoint {String} the backend method name for request
   * @returns {Promise<T>} Generic Promise with defined object in method invocation
   *
   * @usage from components specify the code as below
   * ```typescript
   * const myResponse = await this.restService.get<MyObject>("my-endpoint-here", MyRequestOject);
   * ```
   * the above representation will return a `Promise<MyObject>` since `MyObject` was defined as Generic Object
   */
  public get<T>(endpoint: string): Promise<T> {
    return new Promise((resolve, reject) => {
      this.http.get(this.urlComposer(endpoint), this.getHttpOptions()).subscribe(response => {
        try {
          let data = <ApiResponse>response;
          this.checkResponseStatus(data);
          resolve(<T>data.message);
        }
        catch (error) {
          reject(new Error(getErrorMessage(error)));
        }
      }, (error: HttpErrorResponse) => {
        reject(new Error(this.resolveTranslation(error.error.message)));
      });
    });
  }

  /**
   * ## Rest Service Check Response Status
   * verify that the current response is an error or not.
   * if the response is an error, then the method will `thrown` a regionalizated error key based on backend keys errors for match the specific translation
   * @param data {ApiResponse} object mapped with backend response
   * @returns void
   */
  private checkResponseStatus(data: ApiResponse): void {
    const {
      status, message
    } = data;
    switch (true) {
      case (status === 401):
        this.router.navigate(['/welcome']);
        throw new Error(this.resolveTranslation('INVALID_TOKEN'));
      case (status === 409):
      case (status === 404):
      case (status === 500):
      case (status === 502):
        throw new Error(this.resolveTranslation(message));
      default:
        return;
    }
  }

  private resolveTranslation(message: string): string {
    return this.translateService.instant(getTranslationErrorIndexByKey(message))
  }

}
